/**
 * The add sheet. Reached from a category menu, or straight from the camera
 * button with a photo already on it.
 *
 * If photo recognition is configured, attaching a photo fills the name,
 * category and shelf life while the user watches, and a single Undo puts back
 * exactly what was there before. If it isn't configured, or the call fails, the
 * photo still attaches, the failure is stated in one line, and the fields are
 * typed by hand — nothing about saving depends on the network.
 */

import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

import { CATEGORY_RULES } from "../features/food/categories";
import type { CommonItem } from "../features/food/commonItems";
import { estimateExpiryDate } from "../features/food/expiry";
import type {
  FoodCategory,
  FoodItem,
  FoodItemDraft,
  StorageLocation,
} from "../features/food/types";
import { hasErrors, validateDraft } from "../features/food/validateDraft";
import type { DraftErrors } from "../features/food/validateDraft";
import { isDevBuild, isVisionConfigured } from "../features/vision/config";
import { FAILURE_MESSAGES, recogniseFood } from "../features/vision/recogniseFood";
import type { Recognition } from "../features/vision/types";
import { addDays, todayIso } from "../lib/date/isoDate";
import { createId } from "../lib/storage/foodStorage";
import { CATEGORY_ICONS, Icon } from "./Icon";
import { ItemFields } from "./ItemFields";
import { Sheet } from "./Sheet";

import "./QuickAddItem.css";

/** Where the sheet is in the recognition cycle. */
type RecognitionStatus = "idle" | "working" | "done" | "failed";

/** What Undo puts back. */
interface Snapshot {
  name: string;
  category: FoodCategory;
  location: StorageLocation;
}

interface QuickAddItemProps {
  category: FoodCategory;
  /** A common item to start from, or null for a blank custom item. */
  preset: CommonItem | null;
  /**
   * A photo taken before the sheet opened, from the camera button. Recognition
   * starts on mount, so the sheet is already reading it as it appears.
   */
  initialPhoto?: string | null;
  /** Receives a finished item. The parent saves it and decides when to close. */
  onAdd: (item: FoodItem) => void;
  /**
   * Called instead of filling this sheet when the photo turned out to hold
   * several things. The parent swaps to the review list — one sheet can only
   * add one item, and quietly keeping the first would lose the rest.
   */
  onMultiple?: (results: Recognition[]) => void;
  onClose: () => void;
}

/** Everything the category knows, with the common item's amount layered on top. */
function createDraft(category: FoodCategory, preset: CommonItem | null): FoodItemDraft {
  const rule = CATEGORY_RULES[category];
  return {
    name: preset?.name ?? "",
    category,
    location: rule.defaultLocation,
    quantity: String(preset?.quantity ?? 1),
    unit: preset?.unit ?? rule.defaultUnit,
    purchaseDate: todayIso(),
  };
}

export function QuickAddItem({
  category,
  preset,
  initialPhoto = null,
  onAdd,
  onMultiple,
  onClose,
}: QuickAddItemProps) {
  const [draft, setDraft] = useState<FoodItemDraft>(() => createDraft(category, preset));
  const [errors, setErrors] = useState<DraftErrors>({});
  // Kept out of the draft: it isn't typed, isn't validated, and has its own
  // failure modes, so validateDraft never has to think about it.
  const [photo, setPhoto] = useState<string | null>(initialPhoto);
  /** A shelf life the photo suggested, which wins over the category rule. */
  const [suggestedDays, setSuggestedDays] = useState<number | null>(null);
  const [status, setStatus] = useState<RecognitionStatus>("idle");
  const [failureMessage, setFailureMessage] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const snapshotRef = useRef<Snapshot | null>(null);
  /**
   * True once the user has picked a place themselves. Until then the location is
   * only a guess made from the category, so recognition is free to improve it.
   */
  const locationChosenRef = useRef(false);
  // Lets the async recognition callback read current values without re-running
  // its effect every time a field changes.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    // Anything that arrives already filled in — a common item, or a photo being
    // read right now — needs confirming rather than editing, so focus goes to
    // Add. A blank sheet needs a name typed, so focus goes there instead.
    if (preset || initialPhoto) submitRef.current?.focus();
    else nameInputRef.current?.focus();
    // Deliberately mount-only: moving focus later would fight the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recognition runs when a photo arrives, and is abandoned if the photo is
  // replaced or the sheet closes mid-flight.
  useEffect(() => {
    if (!photo) {
      setStatus("idle");
      setFailureMessage(null);
      return;
    }
    if (!isVisionConfigured()) return;

    // A plain flag rather than an AbortSignal: the request is shared between
    // callers, so walking away means ignoring the answer, not cancelling it.
    let ignore = false;
    setStatus("working");
    setFailureMessage(null);

    recogniseFood(photo).then((outcome) => {
      if (ignore) return;

      if (outcome.status !== "ok") {
        setStatus("failed");
        setFailureMessage(
          outcome.status === "failed" ? FAILURE_MESSAGES[outcome.reason] : null
        );
        return;
      }

      // A shelf, not a jar: hand the whole list up rather than silently
      // filling this sheet with the first thing and losing the other six.
      if (outcome.results.length > 1 && onMultiple) {
        onMultiple(outcome.results);
        return;
      }

      const [first] = outcome.results;
      snapshotRef.current = {
        name: draftRef.current.name,
        category: draftRef.current.category,
        location: draftRef.current.location,
      };
      setDraft((current) => ({
        ...current,
        name: first.name,
        category: first.category,
        // Ice cream recognised from the "Other" menu belongs in the freezer, not
        // wherever "Other" happens to default to — but never overrule a place
        // the user picked on purpose.
        location: locationChosenRef.current
          ? current.location
          : CATEGORY_RULES[first.category].defaultLocation,
      }));
      setSuggestedDays(first.shelfLifeDays);
      setStatus("done");
    });

    return () => {
      ignore = true;
    };
  }, [photo]);

  function undoRecognition() {
    const previous = snapshotRef.current;
    if (!previous) return;

    setDraft((current) => ({ ...current, ...previous }));
    setSuggestedDays(null);
    setStatus("idle");
    snapshotRef.current = null;
    locationChosenRef.current = false;
  }

  function updateField<K extends keyof FoodItemDraft>(field: K, value: FoodItemDraft[K]) {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!(field in current)) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });

    if (field === "location") {
      locationChosenRef.current = true;
      // A suggested shelf life was for the item as photographed. Moving it to
      // the freezer changes the answer by months, so hand the estimate back to
      // the rules table rather than keeping a number that no longer applies.
      setSuggestedDays(null);
    }
  }

  /** A shelf life the photo suggested wins; otherwise the category rule does. */
  const expiry =
    suggestedDays === null
      ? estimateExpiryDate(draft.category, draft.location, draft.purchaseDate)
      : addDays(draft.purchaseDate, suggestedDays);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const foundErrors = validateDraft(draft);
    if (hasErrors(foundErrors)) {
      setErrors(foundErrors);
      return;
    }

    onAdd({
      id: createId(),
      name: draft.name.trim(),
      category: draft.category,
      location: draft.location,
      quantity: Number(draft.quantity),
      unit: draft.unit,
      purchaseDate: draft.purchaseDate,
      estimatedExpiryDate: expiry,
      // Omitted entirely when there's no photo, so items without one keep the
      // exact shape they had before the camera existed.
      ...(photo ? { photo } : {}),
    });
  }

  // Only shown while developing: silence is the right behaviour in a build, but
  // during setup "nothing happened" is impossible to tell from "nothing is
  // configured".
  const showSetupHint = Boolean(photo) && !isVisionConfigured() && isDevBuild();

  return (
    <Sheet
      title={CATEGORY_RULES[draft.category].label}
      icon={CATEGORY_ICONS[draft.category]}
      onClose={onClose}
    >
      {/* noValidate so our messages are the only ones the user sees. */}
      <form
        className="add__form"
        onSubmit={handleSubmit}
        noValidate
        aria-busy={status === "working"}
      >
        <ItemFields
          draft={draft}
          errors={errors}
          photo={photo}
          analyzing={status === "working"}
          nameRef={nameInputRef}
          onField={updateField}
          onPhoto={setPhoto}
        />

        {status !== "idle" && (
          <p className="add__status" data-state={status} role="status">
            <Icon
              name={status === "failed" ? "alert" : status === "working" ? "spinner" : "camera"}
              className="add__status-icon"
            />
            {status === "working" && "Reading the photo…"}
            {status === "failed" && (failureMessage ?? "Couldn't read the photo.")}
            {status === "done" && (
              <>
                Filled from the photo
                <button className="add__undo" type="button" onClick={undoRecognition}>
                  Undo
                </button>
              </>
            )}
          </p>
        )}

        {showSetupHint && (
          <p className="add__status" data-state="hint" role="status">
            <Icon name="alert" className="add__status-icon" />
            Auto-fill is off — set NVIDIA_API_KEY in .env.local and restart.
          </p>
        )}

        <p className="add__hint">Best before {expiry}</p>

        {(errors.name || errors.quantity) && (
          <p className="add__error" role="alert">
            {/* The icon carries the alarm; the sentence stays monochrome. */}
            <Icon name="alert" className="add__error-icon" />
            {errors.name ?? errors.quantity}
          </p>
        )}

        <button className="add__submit" type="submit" ref={submitRef}>
          Add
        </button>
      </form>
    </Sheet>
  );
}
